import { useEffect, useState, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { getApi } from '../../providers/AuthContext';
import CardForm from '../../components/Cards/Form';
import PageWrapper from '../../components/PageWrapper';
import { AlertCircle, ArrowLeft } from 'lucide-react';

const FormSkeleton = () => (
  <div className="form-container">
    <div className="form-wrapper space-y-6">
      <div>
        <div className="skeleton h-4 w-16 mb-3" />
        <div className="skeleton h-12 w-full rounded-xl" />
      </div>
      <div>
        <div className="skeleton h-4 w-24 mb-3" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="skeleton h-40 rounded-xl" />
          ))}
        </div>
      </div>
      <div>
        <div className="skeleton h-4 w-16 mb-3" />
        <div className="skeleton h-12 w-full rounded-xl" />
      </div>
      <div className="flex gap-2">
        <div className="skeleton h-10 w-20 rounded-xl" />
        <div className="skeleton h-10 w-24 rounded-xl" />
      </div>
    </div>
  </div>
);

const Page: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const api = getApi();
  const loadedId = useRef<string | null>(null);

  useEffect(() => {
    if (loadedId.current === id) return;
    loadedId.current = id;

    const loadCard = async () => {
      try {
        setError(null);
        setLoading(true);
        const result = await api.get(`/api/v1/cards/${id}`);
        setCard(result.card);
      } catch (err) {
        setError('Failed to load card.');
      } finally {
        setLoading(false);
      }
    };

    loadCard();
  }, [id]);

  return (
    <PageWrapper pageTitle="Edit Card">
      {loading ? (
        <FormSkeleton />
      ) : error ? (
        <div className="p-4 lg:p-6 max-w-lg mx-auto">
          <div className="p-4 bg-destructive/10 dark:bg-destructive/20 text-destructive border border-destructive/20 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <span className="flex-1 text-sm font-medium">{error}</span>
            <button
              onClick={() => history.push('/cards')}
              className="text-sm font-medium underline text-destructive hover:text-destructive/80 flex items-center gap-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Go Back
            </button>
          </div>
        </div>
      ) : (
        <CardForm card={card} />
      )}
    </PageWrapper>
  )
}

export default Page;
